import classNames from "classnames";
import React, { ReactNode } from "react";
import Pagination, { PaginationProps } from "./Pagination";

export interface TableColumn<T> {
  key: string;
  title: string;
  headerClassName?: string;
  cellClassName?: string;
  render?: (item: T, index: number) => ReactNode;
  mobileRender?: (item: T, index: number) => ReactNode;
}

interface TableProps<T> {
  className?: string;
  columns: TableColumn<T>[];
  data: T[];
  keyExtractor: (item: T) => string | number;
  selectedRowKey?: string | number;
  onRowClick?: (item: T) => void;
  paginationData?: PaginationProps;
}

export default function Table<T>({
  className,
  columns,
  data,
  keyExtractor,
  selectedRowKey,
  onRowClick,
  paginationData,
}: TableProps<T>) {
  return (
    <div className={`overflow-hidden ${className}`}>
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto border">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={classNames(
                    "py-3.5 px-3.5 text-xs font-semibold uppercase tracking-wide text-gray-500",
                    column.headerClassName
                  )}
                >
                  {column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white text-gray-600">
            {data.map((item, index) => (
              <tr
                key={keyExtractor(item)}
                className={`${onRowClick ? "cursor-pointer hover:bg-gray-100" : ""} ${selectedRowKey === keyExtractor(item) ? "bg-blue-50" : ""}`}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {columns.map((column) => (
                  <td
                    key={`${keyExtractor(item)}-${column.key}`}
                    className={classNames(
                      "whitespace-nowrap py-2 px-3.5 text-sm max-w-sm",
                      column.cellClassName
                    )}
                  >
                    {column.render
                      ? column.render(item, index)
                      : (item[column.key as keyof T] as React.ReactNode) || "-"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden">
        <div className="divide-y divide-gray-200 border">
          {data.map((item, index) => {
            const itemKey = keyExtractor(item);
            return (
              <div
                role="button"
                key={itemKey}
                className={`p-4 ${onRowClick ? "cursor-pointer hover:bg-gray-50" : ""} ${selectedRowKey === itemKey ? "bg-blue-50" : ""}`}
                onClick={() => onRowClick && onRowClick(item)}
              >
                {columns.map((column) => (
                  <div
                    key={`${itemKey}-${column.key}`}
                    className="py-2"
                  >
                    <div className="text-xs font-semibold uppercase text-gray-500">
                      {column.title}
                    </div>
                    <div
                      className={classNames(
                        "mt-1 text-sm text-gray-900 min-h-6 truncate",
                        column.cellClassName
                      )}
                    >
                      {column.mobileRender
                        ? column.mobileRender(item, index)
                        : column.render
                          ? column.render(item, index)
                          : (item[column.key as keyof T] as React.ReactNode) ||
                            "-"}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* Pagination */}
      {paginationData && (
        <Pagination
          offset={paginationData.offset}
          limit={paginationData.limit}
          total={paginationData.total}
          setOffset={paginationData.setOffset}
        />
      )}
    </div>
  );
}
